import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { CopyOutlined, KeyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";

const { Text, Paragraph } = Typography;

interface Props {
  open: boolean;
  companyId: number;
  onClose: () => void;
  /** Se dispara tras una emisión aceptada para que el padre refresque
   *  la tabla de tokens. */
  onIssued: () => void;
}

interface FormValues {
  name: string;
  scopes: string[];
  notes?: string;
}

/**
 * Modal de 2 fases para emitir un token Bearer de una `AuditCompany`.
 *
 * Fase 1 — formulario (nombre, scopes, notas).
 * Fase 2 — tras POST exitoso, el backend devuelve el token en claro
 *          EN LA MISMA RESPUESTA (única vez). Pintamos un bloque con
 *          Input.Password + botón copiar + checkbox "Ya lo tengo
 *          guardado" obligatorio para habilitar el botón "Cerrar".
 *
 * No permitimos cerrar la modal con la X ni con Esc en la fase 2 — el
 * admin DEBE confirmar que lo copió antes de perder el secreto para
 * siempre.
 */
export const IssueTokenModal: React.FC<Props> = ({
  open,
  companyId,
  onClose,
  onIssued,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const reset = () => {
    form.resetFields();
    setPlaintext(null);
    setAcknowledged(false);
    setSubmitting(false);
  };

  const handleCancel = () => {
    if (plaintext) {
      // Fase 2 — sólo permitimos cerrar tras confirmar.
      return;
    }
    reset();
    onClose();
  };

  const handleCloseAfterReveal = () => {
    reset();
    onIssued();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const resp = await apiClient.post(
        `/v1/admin/audit-companies/${companyId}/tokens/`,
        {
          name: values.name.trim(),
          scopes: values.scopes,
          notes: values.notes?.trim() || "",
        },
      );
      setPlaintext(resp.data?.token ?? null);
      if (!resp.data?.token) {
        // No debería ocurrir — el backend garantiza el campo en emisión.
        // Si falta, mostramos el fallo sin desactivar la UI de confirmación.
        message.warning(
          t(
            "admin_audit_companies.token_missing_warning",
            "Token emitido pero no se recibió el valor en claro. Rota el token manualmente.",
          ),
        );
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { detail?: string } } })
        .response?.data;
      message.error(
        data?.detail ||
          t("admin_audit_companies.token_issue_failed", "No se pudo emitir el token."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async () => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      message.success(
        t("admin_audit_companies.token_copied", "Token copiado al portapapeles"),
      );
    } catch {
      message.error(
        t("admin_audit_companies.token_copy_failed", "No se pudo copiar automáticamente — selecciónalo y copia a mano"),
      );
    }
  };

  return (
    <Modal
      title={
        <Space>
          <KeyOutlined />
          {plaintext
            ? t("admin_audit_companies.token_reveal_title", "Token emitido — cópialo ahora")
            : t("admin_audit_companies.token_new_title", "Emitir nuevo token")}
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      maskClosable={!plaintext}
      keyboard={!plaintext}
      closable={!plaintext}
      destroyOnClose
      footer={
        plaintext ? (
          <Button
            type="primary"
            disabled={!acknowledged}
            onClick={handleCloseAfterReveal}
          >
            {t("admin_audit_companies.token_close_after_save", "Confirmo que lo he guardado")}
          </Button>
        ) : (
          <Space>
            <Button onClick={handleCancel}>{t("common.cancel", "Cancelar")}</Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              {t("admin_audit_companies.token_issue", "Emitir")}
            </Button>
          </Space>
        )
      }
    >
      {plaintext ? (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Alert
            type="warning"
            showIcon
            message={t(
              "admin_audit_companies.token_warning_title",
              "Este token no se puede recuperar",
            )}
            description={t(
              "admin_audit_companies.token_warning_body",
              "Cópialo ahora y guárdalo en un gestor de contraseñas o pégalo en onetimesecret.com para pasárselo al partner. Después de cerrar este diálogo sólo quedará su hash en la base de datos.",
            )}
          />
          <Input.Password
            value={plaintext}
            readOnly
            visibilityToggle={{ visible: true }}
            addonAfter={
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={copyToClipboard}
              >
                {t("common.copy", "Copiar")}
              </Button>
            }
          />
          <Checkbox
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          >
            {t(
              "admin_audit_companies.token_ack",
              "He guardado el token en un lugar seguro",
            )}
          </Checkbox>
        </Space>
      ) : (
        <Form form={form} layout="vertical" requiredMark={false}>
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            {t(
              "admin_audit_companies.token_form_intro",
              "El token se emite con permisos de lectura externa sobre los datos de esta empresa auditora. Se mostrará una única vez tras guardar.",
            )}
          </Paragraph>
          <Form.Item
            label={t("admin_audit_companies.token_name", "Nombre del token")}
            name="name"
            tooltip={t(
              "admin_audit_companies.token_name_hint",
              "Identificador humano, p. ej. 'IDES (prod)'",
            )}
            rules={[{ required: true, min: 3, max: 100 }]}
          >
            <Input placeholder="IDES (prod)" maxLength={100} />
          </Form.Item>
          <Form.Item
            label={t("admin_audit_companies.token_scopes", "Permisos (scopes)")}
            name="scopes"
            initialValue={["external:audits:read"]}
            rules={[{ required: true, type: "array", min: 1 }]}
          >
            <Select
              mode="tags"
              tokenSeparators={[","]}
              options={[
                { label: "external:audits:read", value: "external:audits:read" },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t("admin_audit_companies.token_notes", "Notas")}
            name="notes"
          >
            <Input.TextArea
              rows={2}
              placeholder={t(
                "admin_audit_companies.token_notes_placeholder",
                "Contacto responsable, propósito, fecha de rotación prevista…",
              )}
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              "admin_audit_companies.token_rate_hint",
              "Rate limit por token: 60 peticiones/minuto.",
            )}
          </Text>
        </Form>
      )}
    </Modal>
  );
};

export default IssueTokenModal;
